import { fg } from '@atlassian/jira-feature-gating';
import { PLAIN_TEXT } from '@atlassian/jira-issue-shared-types/src/common/types/field-schema-type.tsx';
import {
	CORE_PROJECT,
	SOFTWARE_PROJECT,
	SERVICE_DESK_PROJECT,
	PRODUCT_DISCOVERY_PROJECT,
	CUSTOMER_SERVICE_PROJECT,
} from '@atlassian/jira-common-constants/src/project-types.tsx';
import {
	PROJECT_ACTIVE,
	PROJECT_ARCHIVED,
	PROJECT_TRASHED,
} from '@atlassian/jira-issue-shared-types/src/common/types/project-type.tsx';
import {
	PROJECT_GQL_FIELD,
	PROJECT_PICKER_CF_TYPE,
	PROJECT_TYPE,
	SIMILAR_ISSUES_TYPE,
} from '@atlassian/jira-platform-field-config/src/index.tsx';
import type { Edge, FieldTransformer } from '../types/transformer-type.tsx';
import type { SomeJiraIssueField } from '../types/fields-type.tsx';
import type { JiraIssueProjectContext } from '../types/issue-type.tsx';

import { transformAggNodeToLegacyField } from './default-node-transformer.tsx';

const getProjectStatus = (status: NonNullable<JiraIssueProjectContext>['status']) => {
	switch (status) {
		case 'ACTIVE':
			return PROJECT_ACTIVE;
		case 'ARCHIVED':
			return PROJECT_ARCHIVED;
		case 'DELETED':
			return PROJECT_TRASHED;
		default:
			// Based on `getProjectStatus` from src/packages/issue/issue-view/src/common/service/issue/issue-transformer.js
			return PROJECT_ACTIVE;
	}
};

export const getProjectType = (
	projectType: NonNullable<JiraIssueProjectContext>['projectType'],
) => {
	if (projectType === 'CUSTOMER_SERVICE' && fg('jcs_project_type_m3')) {
		return CUSTOMER_SERVICE_PROJECT;
	}

	switch (projectType) {
		case 'SERVICE_DESK':
			return SERVICE_DESK_PROJECT;
		case 'BUSINESS':
			return CORE_PROJECT;
		case 'SOFTWARE':
			return SOFTWARE_PROJECT;
		case 'PRODUCT_DISCOVERY':
			return PRODUCT_DISCOVERY_PROJECT;
		default:
			return null;
	}
};

type MaybeJiraIssueFields =
	| readonly (Edge<SomeJiraIssueField | null | undefined> | null | undefined)[]
	| null
	| undefined;

export const getProjectField = (fields: MaybeJiraIssueFields) => {
	/**
	 * The only way to get the type to return an optional value with `AggJiraProject` type
	 * is to re-assign the type wihtout throwing an error.
	 */

	return fields?.find(
		(edge) =>
			edge?.node?.__typename === PROJECT_GQL_FIELD &&
			edge?.node?.type != null &&
			edge.node.type === PROJECT_TYPE,
	)?.node;
};

export const getSystemAggJiraProject = (fields: MaybeJiraIssueFields) => {
	/**
	 * We only want the system `PROJECT_TYPE` field.
	 * For custom fields, e.g., project picker, see `transformAggProjectToLegacyField`
	 */
	const projectField = getProjectField(fields);

	// Needs the `PROJECT_GQL_FIELD` refinement so we can return as `AggJiraProject`
	if (projectField == null || projectField.__typename !== PROJECT_GQL_FIELD) {
		throw new Error(
			`Failed to get system project data: ${PROJECT_GQL_FIELD} with ${PROJECT_TYPE} 'fieldId' and 'type' not found [${JSON.stringify(
				fields ?? '',
			)}]`,
		);
	}

	if (projectField.project == null) {
		throw new Error(
			`Failed to get system project data: ${PROJECT_GQL_FIELD}.project is missing [${JSON.stringify(
				projectField,
			)}]`,
		);
	}

	return projectField.project;
};

type ProjectWithProjectStyle =
	| { projectStyle: NonNullable<JiraIssueProjectContext>['projectStyle'] }
	| null
	| undefined;

const isAggJiraProjectSimplified = (project: ProjectWithProjectStyle): boolean =>
	project?.projectStyle === 'TEAM_MANAGED_PROJECT';

export const isAggJiraProjectClassic = (project: ProjectWithProjectStyle): boolean =>
	!isAggJiraProjectSimplified(project);

// @ts-expect-error TODO JPX-157
const transformAggProjectToServerProjectOption = (project) => {
	if (project == null) return null;

	const { projectId, key, name, avatar } = project;

	return {
		name,
		id: projectId ?? undefined,
		key,
		avatarUrls: {
			'48x48': avatar?.large || '',
		},
	};
};

export const transformAggSystemProjectToProjectContext = (project: JiraIssueProjectContext) => {
	if (!project) {
		throw new Error('No system project field in response');
	}
	const { projectId, name, key, avatar, status, projectType: type } = project;

	const projectType = getProjectType(type);
	// Remove boardId on cleanup of dfo_remove_navigation_metadata
	let boardId = 'navigationMetadata' in project ? project.navigationMetadata?.boardId : undefined;
	if (fg('dfo_remove_navigation_metadata')) {
		boardId = undefined;
	}

	if (!projectId || !projectType) {
		throw new Error(
			`Failed to transform system project: projectId ${projectId} and projectType ${String(type)} was invalid`,
		);
	}

	return {
		projectId,
		projectType,
		projectName: name,
		projectKey: key,
		projectStatus: getProjectStatus(status),
		avatarUrl: avatar?.medium,
		isSimplified: isAggJiraProjectSimplified(project),
		boardId,
	};
};

export const transformAggProjectToProjectContext = (fields: MaybeJiraIssueFields) => {
	const project = getSystemAggJiraProject(fields);

	return transformAggSystemProjectToProjectContext(project);
};

const transformAggProjectToLegacyProjectPickerField: FieldTransformer = (node) => ({
	...transformAggNodeToLegacyField(node),
	allowedValues: [],
	value: transformAggProjectToServerProjectOption(node.project),
	schema: {
		configuration: null,
		items: null,
		renderer: PLAIN_TEXT,
		type: PROJECT_TYPE,
		custom: PROJECT_PICKER_CF_TYPE,
		system: null,
	},
});

// @ts-expect-error TODO JPX-157
const transformAggProjectToLegacySimilarIssuesField = (node) => {
	// Similar issues field only exist in JSM projects
	if (getProjectType(node.project?.projectType) !== SERVICE_DESK_PROJECT) return undefined;

	return {
		...transformAggNodeToLegacyField(node),
		key: SIMILAR_ISSUES_TYPE,
		title: SIMILAR_ISSUES_TYPE,
		value: node.project?.similarIssues?.featureEnabled === true,
		schema: {
			configuration: null,
			items: null,
			renderer: null,
			type: SIMILAR_ISSUES_TYPE,
			custom: null,
			system: SIMILAR_ISSUES_TYPE,
		},
	};
};

// @ts-expect-error TODO JPX-157
const transformAggProjectToLegacyProjectField = (node) => {
	if (
		node?.__typename !== PROJECT_GQL_FIELD ||
		node?.type === null ||
		node?.type !== PROJECT_TYPE
	) {
		throw new Error(
			`Failed to get system project data: ${PROJECT_GQL_FIELD} with ${PROJECT_TYPE} 'fieldId' and 'type' not found [${JSON.stringify(
				node,
			)}]`,
		);
	}

	const projectField = node;

	if (projectField == null || projectField.__typename !== PROJECT_GQL_FIELD) {
		throw new Error(
			`Failed to get system project data: ${PROJECT_GQL_FIELD} with ${PROJECT_TYPE} 'fieldId' and 'type' not found [${JSON.stringify(
				node ?? '',
			)}]`,
		);
	}

	if (projectField.project == null) {
		throw new Error(
			`Failed to get system project data: ${PROJECT_GQL_FIELD}.project is missing [${JSON.stringify(
				projectField,
			)}]`,
		);
	}

	const project = projectField.project;
	const { projectType: type } = project;
	const projectType = getProjectType(type);

	if (projectType == null) {
		throw new Error(
			`Failed to transform project: projectType ${String(project?.projectType)} was invalid`,
		);
	}

	return {
		...transformAggNodeToLegacyField(node),
		value: {
			projectType,
		},
	};
};

// @ts-expect-error TODO JPX-157
const transformAggProjectToLegacyFieldByType = (node) => {
	const projectType = getProjectType(node.project?.projectType);

	if (projectType === SERVICE_DESK_PROJECT) {
		// Similar issues field only exist in JSM projects
		return transformAggProjectToLegacySimilarIssuesField(node);
	}

	if (projectType === PRODUCT_DISCOVERY_PROJECT) {
		return transformAggProjectToLegacyProjectField(node);
	}

	return undefined;
};

export const transformAggProjectToLegacyField: FieldTransformer<
	typeof PROJECT_PICKER_CF_TYPE | typeof PROJECT_TYPE | unknown
> = (node) => {
	if (fg('jpd_idea_panel_in_issue_view')) {
		switch (node.type) {
			case PROJECT_PICKER_CF_TYPE:
				return transformAggProjectToLegacyProjectPickerField(node);
			case PROJECT_TYPE:
				// For the system project, see `transformAggProjectToProjectContext`
				return transformAggProjectToLegacyFieldByType(node);
			default:
				return undefined;
		}
	} else {
		switch (node.type) {
			case PROJECT_PICKER_CF_TYPE:
				return transformAggProjectToLegacyProjectPickerField(node);
			case PROJECT_TYPE:
				// For the system project, see `transformAggProjectToProjectContext`
				return transformAggProjectToLegacySimilarIssuesField(node);
			default:
				return undefined;
		}
	}
};
